import { ref, toValue, watch } from 'vue'
import type { Comment } from './use-comments'
import usePut from './api/usePut'
import { ApiRequestStatus } from '../models/api/api-request-status'
import { useSnackStore } from '../../stores/snack.store'
import { useI18n } from 'vue-i18n'
import type { MaybeRef, Mutable } from '@vueuse/core'
import useDelete from './api/useDelete'
import { useDialog } from '../components/base/dialog/use-dialog'

export const useComment = (
  initialValue: MaybeRef<Comment>,
  updateCallback?: (comment: Comment) => void,
  deleteCallback?: () => void,
) => {
  const { t } = useI18n()
  const snackStore = useSnackStore()

  const { mutate: updateComment, status: statusUpdateComment } = usePut<Partial<Comment>>(
    `comment/${toValue(initialValue).id}`,
  )
  const { remove: removeComment, status: statusRemoveComment } = useDelete<string>(
    `comment/${toValue(initialValue).id}`,
  )
  const { mutate: promoteToConversationNoteMutate, status: promoteStatus } = usePut(
    `comment/${toValue(initialValue).id}/conversation-note`,
  )

  const isEditing = ref(false)
  const editableComment = ref<Mutable<Comment>>({ ...toValue(initialValue) })

  watch(initialValue, (newInitialValue) => {
    editableComment.value = { ...toValue(newInitialValue) }
  })

  const cancelEdit = () => {
    isEditing.value = false
    editableComment.value.text = toValue(initialValue).text
  }

  const editComment = () => {
    isEditing.value = true
  }

  const saveComment = async () => {
    await updateComment({
      text: editableComment.value.text,
      isShared: editableComment.value.isShared,
    })

    if (statusUpdateComment.value == ApiRequestStatus.Success) {
      isEditing.value = false
      if (updateCallback) {
        updateCallback(editableComment.value)
      }
      editableComment.value = { ...toValue(initialValue) }
      snackStore.showInfo(t('comment.update.success'))
    }
    if (statusUpdateComment.value == ApiRequestStatus.Error) {
      snackStore.showError(t('comment.update.error'))
    }
  }

  const {
    isOpen: isOpenDeleteCommentDialog,
    openDialog: openDeleteCommentDialog,
    closeDialog: closeDeleteCommentDialog,
  } = useDialog()

  const deleteComment = async () => {
    await removeComment({})

    closeDeleteCommentDialog()

    if (statusRemoveComment.value == ApiRequestStatus.Success) {
      snackStore.showInfo(t('comment.delete.success'))
      if (deleteCallback) {
        deleteCallback()
      }
    }
    if (statusRemoveComment.value == ApiRequestStatus.Error) {
      snackStore.showError(t('comment.delete.error'))
    }
  }

  const promoteToConversationNote = async () => {
    await promoteToConversationNoteMutate({})

    if (promoteStatus.value == ApiRequestStatus.Success) {
      const promotedComment: Comment = {
        ...toValue(initialValue),
        isConversationNote: true,
        isShared: true,
      }
      editableComment.value = { ...promotedComment }
      if (updateCallback) {
        updateCallback(promotedComment)
      }
      snackStore.showInfo(t('comment.conversation-note.promote.success'))
    }
    if (promoteStatus.value == ApiRequestStatus.Error) {
      snackStore.showError(t('comment.conversation-note.promote.error'))
    }
  }

  return {
    isEditing,
    editableComment,
    cancelEdit,
    saveComment,
    editComment,
    deleteComment,
    promoteToConversationNote,
    isOpenDeleteCommentDialog,
    openDeleteCommentDialog,
    closeDeleteCommentDialog,
  }
}
